import React, { useEffect, useState } from "react";
import ImageDropzone from "@/components/ImageDropzone";
import SystemCategoriesEditor from "@/components/SystemCategoriesEditor";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { AssessmentsAPI, AssetsAPI, API } from "@/lib/api";
import { useAuth } from "@/lib/providers";

// Same model table as NewVariationSet — Flash is default. Heavier models trigger a confirm popup.
const MODELS = [
  { provider: "gemini",    model: "gemini-2.5-flash",            label: "Gemini 2.5 Flash",  tagline: "Fastest · lightest on credits · default", credits_per_call: 0.0005, recommended: true },
  { provider: "gemini",    model: "gemini-2.5-pro",              label: "Gemini 2.5 Pro",    tagline: "Solid all-round · ~50× more credits than Flash", credits_per_call: 0.025 },
  { provider: "anthropic", model: "claude-haiku-4-5-20251001",   label: "Claude Haiku 4.5",  tagline: "Sharper visual reading · ~40× more credits than Flash", credits_per_call: 0.021 },
  { provider: "anthropic", model: "claude-sonnet-4-5-20250929",  label: "Claude Sonnet 4.5", tagline: "Most nuanced · ~120× more credits than Flash", credits_per_call: 0.062 },
];
const DEFAULT_MODEL = MODELS.find((m) => m.recommended);

function defaultTitle(kind, project) {
  const date = new Date().toLocaleDateString();
  if (kind === "sub_premise") return `Sub-premise / variant — ${date}`;
  if (kind === "system_level") return `System architecture review — ${date}`;
  return `${project.brand_name} pack v1`;
}

export default function NewAssessment({ project, kind, assessments, duplicateSeed, onClose, onCreated }) {
  const { user } = useAuth();
  // Prefill state from a duplicated assessment (P1) when the caller passed
  // `duplicateSeed`. Fields cloned: title (with " (copy)" suffix so the
  // designer knows this is derived), sub-premise name, compare-against link,
  // extra context, and all image blobs (pack / competitor / existing pack).
  // Uploaded blobs are wrapped as { b64, mime } so they slot straight into
  // the existing packFiles / compFiles / existingPackFiles state shape.
  const seedFiles = (blobs, mimes) => (blobs || []).map((b64, i) => {
    const mime = (mimes && mimes[i]) || "image/png";
    return {
      b64,
      mime,
      name: `duplicated-${i + 1}.${mime.split("/")[1] || "png"}`,
      preview: `data:${mime};base64,${b64}`,
      size: 0,
    };
  });

  const [title, setTitle] = useState(() => {
    if (duplicateSeed?.title) return `${duplicateSeed.title} (copy)`;
    return defaultTitle(kind, project);
  });
  const [subName, setSubName] = useState(duplicateSeed?.sub_premise_name || "");
  const [compareTo, setCompareTo] = useState(duplicateSeed?.compare_against_assessment_id || "");
  const [designerName, setDesignerName] = useState(duplicateSeed?.designer_name || user?.name || "");
  const [packFiles, setPackFiles] = useState(() => seedFiles(duplicateSeed?.pack_images_b64, duplicateSeed?.pack_image_mimes));
  const [existingPackFiles, setExistingPackFiles] = useState(() => seedFiles(duplicateSeed?.existing_pack_images_b64, duplicateSeed?.existing_pack_image_mimes));
  const [compFiles, setCompFiles] = useState(() => seedFiles(duplicateSeed?.competitor_images_b64, duplicateSeed?.competitor_image_mimes));
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [pickedLibraryIds, setPickedLibraryIds] = useState([]);
  const initialModelKey = duplicateSeed?.model_provider && duplicateSeed?.model_name
    ? `${duplicateSeed.model_provider}|${duplicateSeed.model_name}`
    : `${DEFAULT_MODEL.provider}|${DEFAULT_MODEL.model}`;
  const [modelKey, setModelKey] = useState(initialModelKey);
  const [pendingModelKey, setPendingModelKey] = useState(null);
  const [extra, setExtra] = useState(duplicateSeed?.extra_context || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Safety-net paywall — if the balance fetched up-front at the project page
  // was stale and the backend 402s on submit, we open the same modal here so
  // the user doesn't get a cryptic error toast.
  const [showPaywall, setShowPaywall] = useState(false);
  // System-level categories editor. Duplicate seeds preserve categories too —
  // each category's images come back as base64 blobs which we wrap into the
  // {b64, mime, preview} shape used by ImageDropzone.
  const [categories, setCategories] = useState(() => {
    const seedCats = duplicateSeed?.categories;
    if (seedCats && Array.isArray(seedCats) && seedCats.length >= 2) {
      return seedCats.map((c, idx) => ({
        id: `cat_seed_${idx}_${Math.random().toString(36).slice(2, 6)}`,
        name: c.name || "",
        packFiles: seedFiles(c.pack_images_b64, c.pack_image_mimes),
        compFiles: seedFiles(c.competitor_images_b64, c.competitor_image_mimes),
      }));
    }
    return kind === "system_level"
      ? [
          { id: `cat_${Date.now()}_a`, name: "", packFiles: [], compFiles: [] },
          { id: `cat_${Date.now()}_b`, name: "", packFiles: [], compFiles: [] },
        ]
      : [];
  });

  const tryPickModel = (m) => {
    const key = `${m.provider}|${m.model}`;
    if (key === modelKey) return;
    if (m.recommended) setModelKey(key);
    else setPendingModelKey(key);
  };
  const confirmPendingModel = () => {
    if (pendingModelKey) setModelKey(pendingModelKey);
    setPendingModelKey(null);
  };
  const pendingModel = pendingModelKey
    ? MODELS.find((m) => `${m.provider}|${m.model}` === pendingModelKey)
    : null;

  useEffect(() => {
    AssetsAPI.list(project.project_id).then(({ data }) => setLibraryAssets(data || [])).catch(() => setLibraryAssets([]));
  }, [project.project_id]);

  const togglePick = (id) => {
    setPickedLibraryIds((xs) => xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]);
  };

  const isRefreshProject = project?.project_type === "packaging_refresh";
  const projectHasExistingPack = !!(project?.existing_pack_images_b64 && project.existing_pack_images_b64.length);
  // P3/P4 — Existing pack becomes mandatory for sub-premise audits and for
  // primary audits on a `packaging_refresh` project. `system_level` still
  // ignores the existing-pack field (it has its own architecture pipeline).
  // If the project already carries the existing pack (captured at project
  // creation), we don't re-ask for it here — the backend auto-inherits.
  const existingPackRequired =
    kind === "sub_premise" ||
    (kind === "primary" && isRefreshProject && !projectHasExistingPack);
  const showExistingPackSection = existingPackRequired;

  const submit = async () => {
    setError("");
    if (kind === "system_level") {
      // Validate the system-level payload — ≥2 categories, each named, each
      // with at least one brand pack image.
      if (categories.length < 2) {
        setError("System-level audits require at least 2 categories.");
        return;
      }
      for (const c of categories) {
        if (!(c.name || "").trim()) { setError("Every category needs a name."); return; }
        if (!c.packFiles.length) { setError(`Category "${c.name}" needs at least one brand pack image.`); return; }
      }
      const [provider, model] = modelKey.split("|");
      setSubmitting(true);
      try {
        const { data } = await AssessmentsAPI.create({
          project_id: project.project_id,
          title,
          assessment_kind: "system_level",
          designer_name: designerName,
          model_provider: provider,
          model_name: model,
          pack_images_b64: [],
          pack_image_mimes: [],
          extra_context: extra,
          categories: categories.map((c) => ({
            name: c.name,
            pack_images_b64: c.packFiles.map((f) => f.b64),
            pack_image_mimes: c.packFiles.map((f) => f.mime),
            competitor_images_b64: c.compFiles.map((f) => f.b64),
            competitor_image_mimes: c.compFiles.map((f) => f.mime),
          })),
        });
        onCreated(data.assessment_id);
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        if (e?.response?.status === 402) { setShowPaywall(true); setError(""); }
        else setError(detail);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!packFiles.length) {
      setError("Upload at least one pack image.");
      return;
    }
    if (existingPackRequired && !existingPackFiles.length) {
      setError(
        kind === "sub_premise"
          ? "Upload the existing (parent/previous) pack — Verdict scores the variant against it."
          : "This is a Packaging Refresh project. Upload the current on-shelf pack so Verdict can score improvement across all 7 dimensions."
      );
      return;
    }
    const [provider, model] = modelKey.split("|");
    setSubmitting(true);
    try {
      // Pull picked library assets in a single bulk fetch
      let libCompetitors = [];
      if (pickedLibraryIds.length > 0) {
        const { data } = await AssetsAPI.bulkFetch(project.project_id, pickedLibraryIds);
        libCompetitors = (data?.assets || []).map((x) => ({ data_b64: x.data_b64, mime: x.mime }));
      }
      const allCompB64 = [...compFiles.map((f) => f.b64), ...libCompetitors.map((x) => x.data_b64)];
      const allCompMime = [...compFiles.map((f) => f.mime), ...libCompetitors.map((x) => x.mime)];
      const { data } = await AssessmentsAPI.create({
        project_id: project.project_id,
        title,
        assessment_kind: kind,
        sub_premise_name: subName,
        compare_against_assessment_id: compareTo || null,
        designer_name: designerName,
        model_provider: provider,
        model_name: model,
        pack_images_b64: packFiles.map((f) => f.b64),
        pack_image_mimes: packFiles.map((f) => f.mime),
        competitor_images_b64: allCompB64,
        competitor_image_mimes: allCompMime,
        existing_pack_images_b64: existingPackFiles.map((f) => f.b64),
        existing_pack_image_mimes: existingPackFiles.map((f) => f.mime),
        extra_context: extra,
      });
      onCreated(data.assessment_id);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (e?.response?.status === 402) {
        setShowPaywall(true);
        setError("");
      } else {
        setError(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" data-testid="new-assessment-modal" style={{
      position: "fixed", inset: 0, background: "color-mix(in srgb, var(--v-bg) 70%, transparent)",
      backdropFilter: "blur(8px)", zIndex: 80, overflow: "auto",
    }}>
      <div className="v-container" style={{ paddingTop: 48, paddingBottom: 80 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="overline">
            {duplicateSeed ? "Duplicate & edit" : kind === "primary" ? "New assessment" : kind === "sub_premise" ? "Sub-premise / variant" : "System-level architecture"}
          </div>
          <button className="v-btn v-btn-outline" data-testid="close-assessment-modal" onClick={onClose}>Close ×</button>
        </div>
        <h1 className="display" style={{ fontSize: "clamp(32px,4vw,52px)", marginTop: 8, lineHeight: 1 }}>
          {duplicateSeed
            ? "Tweak the inputs and re-run."
            : kind === "system_level" ? "Stress-test the pack architecture." : kind === "sub_premise" ? "Compare a new variant to the parent pack." : "Run the verdict."}
        </h1>
        {duplicateSeed && (
          <div className="v-surface" style={{ padding: 14, marginTop: 20, borderLeft: "3px solid var(--v-accent)" }} data-testid="duplicate-banner">
            <div className="overline" style={{ color: "var(--v-accent)" }}>Duplicated from</div>
            <div style={{ marginTop: 4, fontSize: 14 }}>
              <b>{duplicateSeed.title}</b>
              <span className="mono" style={{ color: "var(--v-text-2)", marginLeft: 8, fontSize: 11 }}>
                · {duplicateSeed.overall_score !== undefined && duplicateSeed.overall_score !== null ? `previous ${Number(duplicateSeed.overall_score).toFixed(1)}` : "not yet scored"}
              </span>
            </div>
            <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11, marginTop: 6, lineHeight: 1.55 }}>
              All images, context and settings have been carried over. Edit anything below and click Run Verdict — a fresh assessment will be created; the original stays intact.
            </div>
          </div>
        )}

        <div className="v-surface" style={{ padding: 28, marginTop: 32 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Assessment title" value={title} onChange={setTitle} testId="field-title" />
            <Field label="Designer name (records who uploaded)" value={designerName} onChange={setDesignerName} testId="field-designer" />
            {kind === "sub_premise" && (
              <>
                <Field label="Sub-premise / variant name" value={subName} onChange={setSubName} placeholder="e.g. Mango variant, Premium tier" testId="field-sub-premise-name" />
                <div>
                  <div className="overline" style={{ marginBottom: 6 }}>Compare against previous assessment</div>
                  <select className="v-select" value={compareTo} onChange={(e) => setCompareTo(e.target.value)} data-testid="field-compare-against">
                    <option value="">— None —</option>
                    {assessments.filter((a) => a.assessment_kind !== "sub_premise" || a.assessment_id).map((a) => (
                      <option key={a.assessment_id} value={a.assessment_id}>{a.title} ({Number(a.overall_score).toFixed(1)})</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div style={{ gridColumn: "1 / -1" }}>
              <div className="overline" style={{ marginBottom: 6 }}>AI critic — pick by credit cost vs nuance</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="assessment-model-card-picker">
                {MODELS.map((m) => {
                  const key = `${m.provider}|${m.model}`;
                  const isSel = key === modelKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => tryPickModel(m)}
                      data-testid={`assessment-model-card-${m.model}`}
                      style={{
                        textAlign: "left", padding: "14px 16px",
                        background: isSel ? "color-mix(in srgb, var(--v-accent) 8%, transparent)" : "var(--v-bg)",
                        border: isSel ? "1px solid var(--v-accent)" : "1px solid var(--v-border)",
                        cursor: "pointer", borderRadius: 2, position: "relative",
                      }}
                    >
                      {m.recommended && (
                        <div className="overline" style={{ position: "absolute", top: 8, right: 12, fontSize: 9, color: "var(--v-accent)" }}>Default</div>
                      )}
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--v-text)" }}>{m.label}</div>
                      <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11, marginTop: 4, letterSpacing: ".06em" }}>{m.tagline}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="overline" style={{ marginBottom: 6 }}>Extra context (optional)</div>
            <textarea className="v-textarea" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Anything specific you want Verdict to weigh in on…" data-testid="field-extra" />
          </div>

          {kind === "system_level" ? (
            <div style={{ marginTop: 32 }}>
              <SystemCategoriesEditor categories={categories} setCategories={setCategories} />
            </div>
          ) : (
            <>
              <div style={{ marginTop: 28 }}>
                <ImageDropzone files={packFiles} setFiles={setPackFiles} label="Pack design images (required)" testId="dropzone-pack" />
              </div>
              {showExistingPackSection && (
                <div style={{ marginTop: 24 }} data-testid="existing-pack-section">
                  <ImageDropzone
                    files={existingPackFiles}
                    setFiles={setExistingPackFiles}
                    label={
                      kind === "sub_premise"
                        ? "Existing (parent) packaging design — REQUIRED"
                        : "Current on-shelf pack (before refresh) — REQUIRED"
                    }
                    testId="dropzone-existing-pack"
                  />
                  <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
                    {kind === "sub_premise"
                      ? "Upload the parent / previous pack. Verdict will score the variant against it and add a side-by-side refresh section to the report."
                      : "This is a Packaging Refresh project. Upload the current on-shelf pack — Verdict will score improvement across all 7 dimensions against it."}
                  </div>
                </div>
              )}
              {kind === "primary" && isRefreshProject && projectHasExistingPack && (
                <div className="v-surface" style={{ marginTop: 24, padding: 14, borderLeft: "3px solid var(--v-accent)" }} data-testid="inherited-existing-pack-notice">
                  <div className="overline" style={{ color: "var(--v-accent)" }}>Existing pack · inherited from project</div>
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    Verdict will score improvement against the {project.existing_pack_images_b64.length} existing on-shelf pack image{project.existing_pack_images_b64.length === 1 ? "" : "s"} you uploaded when creating this project.
                  </div>
                </div>
              )}
              <div style={{ marginTop: 24 }}>
                <ImageDropzone files={compFiles} setFiles={setCompFiles} label="Competitor references (optional — upload new)" testId="dropzone-competitor" />
              </div>

              {libraryAssets.length > 0 && (
            <div style={{ marginTop: 24 }} data-testid="library-picker">
              <div className="overline" style={{ marginBottom: 8 }}>
                Or pick from project library · {pickedLibraryIds.length} selected
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {libraryAssets.slice(0, 24).map((a) => {
                  const picked = pickedLibraryIds.includes(a.asset_id);
                  return (
                    <button
                      key={a.asset_id}
                      type="button"
                      onClick={() => togglePick(a.asset_id)}
                      data-testid={`library-pick-${a.asset_id}`}
                      style={{
                        position: "relative", padding: 0, background: "transparent",
                        border: picked ? "2px solid var(--v-accent)" : "1px solid var(--v-border)",
                        borderRadius: 4, cursor: "pointer", aspectRatio: "1/1", overflow: "hidden",
                      }}
                    >
                      <img
                        src={`${API}/projects/${project.project_id}/assets/${a.asset_id}?download=1`}
                        alt={a.label}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <div className="mono" style={{
                        position: "absolute", left: 0, right: 0, bottom: 0,
                        background: "linear-gradient(to top, rgba(0,0,0,.75), transparent)",
                        color: "#fff", padding: "10px 4px 3px", fontSize: 9, textAlign: "left",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {a.kind.replace("_", " ")} · {a.label || "—"}
                      </div>
                      {picked && (
                        <div style={{ position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: 999, background: "var(--v-accent)", color: "var(--v-bg)", fontSize: 12, lineHeight: "18px", textAlign: "center", fontWeight: 700 }}>✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
            </>
          )}

          {error && <div className="mono" style={{ color: "var(--v-accent)", marginTop: 16 }} data-testid="assessment-error">{error}</div>}

          <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="mono" style={{ color: "var(--v-text-2)", fontSize: 12 }}>
              Analysis runs across all 7 dimensions. Takes ~20–40 seconds.
            </div>
            <button className="v-btn" data-testid="submit-assessment-btn" disabled={submitting} onClick={submit}>
              {submitting ? "Analysing…" : "Run Verdict →"}
            </button>
          </div>
        </div>
      </div>

      {pendingModel && (
        <AssessmentHeavyModelWarning
          pending={pendingModel}
          defaultModel={DEFAULT_MODEL}
          onCancel={() => setPendingModelKey(null)}
          onConfirm={confirmPendingModel}
        />
      )}
      <BuyCreditsModal open={showPaywall} onClose={() => setShowPaywall(false)} creditsAvailable={0} />
    </div>
  );
}

function AssessmentHeavyModelWarning({ pending, defaultModel, onCancel, onConfirm }) {
  const ratio = Math.max(1, Math.round(pending.credits_per_call / (defaultModel.credits_per_call || 0.0005)));
  return (
    <div
      role="dialog"
      data-testid="assessment-heavy-model-warning"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, background: "color-mix(in srgb, var(--v-bg) 60%, rgba(0,0,0,.4))",
        backdropFilter: "blur(6px)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="v-surface"
        style={{ maxWidth: 500, width: "100%", padding: 28, borderLeft: "3px solid #F59E0B" }}
      >
        <div className="overline" style={{ color: "#F59E0B" }}>Heads up · heavier credit usage</div>
        <h2 style={{ fontSize: 22, marginTop: 6, lineHeight: 1.2 }}>
          {pending.label} consumes ~{ratio}× more credits than {defaultModel.label}.
        </h2>
        <p style={{ marginTop: 14, color: "var(--v-text-2)", fontSize: 13, lineHeight: 1.6 }}>
          {pending.tagline}. Use this critic only when the extra nuance is worth the credit spend —
          it pulls from your <b>monthly credit allowance</b>.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 22, justifyContent: "flex-end" }}>
          <button className="v-btn v-btn-outline" onClick={onCancel} data-testid="assessment-heavy-cancel">
            Stay with {defaultModel.label}
          </button>
          <button className="v-btn" onClick={onConfirm} data-testid="assessment-heavy-confirm" style={{ background: "#F59E0B", borderColor: "#F59E0B", color: "#0a0a0a" }}>
            Use {pending.label} anyway →
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <div className="overline" style={{ marginBottom: 6 }}>{label}</div>
      <input className="v-input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}
